"use client";

import React from "react";
import classNames from "classnames";
import CheckIcon from "@repo/ui/branded/check-icon";

export interface CheckboxProps {
  /** Whether the checkbox is checked */
  checked: boolean;
  /** Callback fired when the checkbox is toggled */
  onChange: (checked: boolean) => void;
  /** Whether the checkbox is disabled */
  disabled?: boolean;
  /** Accessible label */
  "aria-label"?: string;
  /** Additional CSS classes on the outer label */
  className?: string;
  /** HTML id for label association */
  id?: string;
  "data-testid"?: string;
}

/**
 * Checkbox using a native <input type="checkbox"> so that <label htmlFor>
 * association, :disabled, and standard selectors all work without
 * workarounds. The native input is visually hidden and a custom visual proxy
 * renders the appearance via peer-checked.
 *
 * Visual divergence between brands is handled in two layers, never inline:
 *   - Box (radius, border width): CSS variables in brands/{kinsa,owlet}.css
 *     (`--brand-checkbox-radius`, `--brand-checkbox-border-width`).
 *   - Check glyph: `<CheckIcon />` resolves to the brand-specific SVG via the
 *     `@repo/ui/branded` alias. This component stays brand-agnostic.
 */
export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  onChange,
  disabled = false,
  "aria-label": ariaLabel,
  className,
  id,
  "data-testid": dataTestId,
}) => {
  return (
    <span
      className={classNames(
        "relative inline-flex size-4 shrink-0 items-center justify-center",
        disabled && "cursor-not-allowed opacity-40",
        className,
      )}
    >
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        className="peer absolute inset-0 z-10 appearance-none cursor-pointer"
      />
      <div
        className={classNames(
          "h-full w-full flex items-center justify-center transition-colors",
          "rounded-[var(--brand-checkbox-radius,4px)]",
          "border-solid border-[length:var(--brand-checkbox-border-width,1px)]",
          "peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2",
          checked
            ? "border-primary bg-primary"
            : "border-[var(--control-border)] bg-transparent",
        )}
      >
        {checked ? <CheckIcon className="text-on-primary" /> : null}
      </div>
    </span>
  );
};

export default Checkbox;
